import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Button,
  Grid,
  Container,
  Typography,
  Backdrop,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import './FinishedJobsDetailPage.css';
import Services from 'src/services/Services';
import { useLocalization } from 'src/context/LocalizationContext';
import { StatusList } from 'src/types/statusTypes';

const POST_STATUS_MAP = StatusList.reduce((acc, status) => {
  acc[status.id] = status.label;
  return acc;
}, {});

export default function HandshakeDetailPage() {
  const { language } = useLocalization();
  const navigate = useNavigate();
  const { handshakeID, documentNo } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchData = () => {
    setLoading(true);
    Services.GetIrsaliyeDetail(handshakeID)
      .then((response) => {
        if (response.data.status && response.data.result) {
          const resultArray = Array.isArray(response.data.result)
            ? response.data.result
            : [response.data.result];

          // Sondaki "(2)", "(ç)" gibi ekleri at ve Unicode'u normalize et (İ vb.)
          const normalizeDocNo = (value) =>
            (value || '')
              .replace(/\s*\([^)]*\)\s*$/, '')
              .trim()
              .normalize('NFC');
          const targetDocNo = normalizeDocNo(documentNo);

          const selectedItem =
            resultArray.find(
              (item) => normalizeDocNo(item.documentNo) === targetDocNo
            ) || (resultArray.length === 1 ? resultArray[0] : null);

          if (selectedItem) {
            setData(selectedItem);
          } else {
            setData(null);
          }
        } else {
          setData(null);
        }
      })
      .catch((error) => {
        console.error('İrsaliye detay hatası:', error);
        setData(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [handshakeID, documentNo]);

  // --- Yardımcı Fonksiyonlar ---

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return moment.unix(timestamp).format('DD.MM.YYYY - HH:mm');
  };

  const formatConfirmationType = (type) => {
    const types = {
      'static-qr': 'Statik QR',
      'dynamic-qr': 'Dinamik QR',
      manual: 'Manuel',
    };
    return types[type] || type;
  };

  const formatDirection = (direction, fromLocationType) => {
    if (direction === 'StoreToMallLogistics') return 'Mağazadan Mal Kabule';
    if (direction === 'MallLogisticsToStore') return 'Mal Kabulden Mağazaya';
    if (direction === 'StoreToStore') {
      if (fromLocationType === 'store') return 'Mağazadan Depoya';
      if (fromLocationType === 'warehouse') return 'Depodan Mağazaya';
    }
    return direction;
  };

  const formatFromLocationType = (type) => {
    const types = {
      store: 'Mağaza',
      'mall-logistics': 'AVM Lojistik',
    };
    return types[type] || type;
  };

  const formatPostStatus = (status) => {
    return POST_STATUS_MAP[Number(status)] || status;
  };

  // İsim Maskeleme: İlk 2 harf + 3 yıldız
  const maskName = (str) => {
    if (!str) return '*****';
    if (str.length < 2) return str + '***';
    return str.substring(0, 2) + '***';
  };

  // Fiyat Hesaplama Mantığı
  const calculatePricing = () => {
    if (!data || !data.details || !data.transportationToolWithPrice) {
      return { rows: [], totalSum: 0, currencySymbol: 'TRY' };
    }

    let totalSum = 0;
    let currencySymbol = 'TRY';

    const rows = data.details.map((detail, index) => {
      const toolName = detail.transportationTool || '';
      const count = parseFloat(detail.transportationToolCount) || 0;

      // Eşleşme Mantığı: "Ring" veya "Koli" geçiyorsa "Koli / Kargo" fiyatını al
      let matchTitle = toolName;
      if (toolName.includes('Ring') || toolName.includes('Koli')) {
        matchTitle = 'Koli / Kargo';
      }

      // Fiyat listesinden eşleşeni bul
      const priceObj =
        data.transportationToolWithPrice.find((p) => p.title === matchTitle) ||
        data.transportationToolWithPrice.find((p) => p.title === toolName);

      let unitPrice = 0;
      if (priceObj && priceObj.price) {
        // "259,62" formatını 259.62 float formatına çevir
        unitPrice = parseFloat(priceObj.price.replace(',', '.'));
        currencySymbol = priceObj.currency;
      }

      const rowTotal = count * unitPrice;
      totalSum += rowTotal;

      return {
        id: index,
        toolName: toolName,
        count: count,
        unitPrice: unitPrice,
        total: rowTotal,
        currency: currencySymbol,
      };
    });

    return { rows, totalSum, currencySymbol };
  };

  const pricingData = calculatePricing();

  if (loading) {
    return (
      <Container>
        <Backdrop sx={{ color: '#fff', zIndex: 2 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container>
        <Typography variant="h5" sx={{ mt: 4 }}>
          Veri bulunamadı
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/dashboard/handshakes-report-list')}
          sx={{ mt: 2 }}
        >
          Geri
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>İrsaliye Detay | Mall Logistics Panel</title>
      </Helmet>

      <Container style={{ marginBottom: 30 }}>
        <Button
          variant="contained"
          onClick={() => navigate('/dashboard/handshakes-report-list')}
          style={{ marginBottom: 20 }}
        >
          Geri
        </Button>

        <Grid container spacing={2}>
          {/* SOL TARAF: İRSALİYE DETAY BİLGİLERİ (Tüm alanlar korundu) */}
          <Grid item xs={12} md={7}>
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                paddingBottom: '29px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                height: '100%', // Yükseklik hizalaması için
              }}
            >
              <div
                style={{
                  minHeight: '50px',
                  width: '100%',
                  backgroundColor: '#172955',
                  borderTopRightRadius: '8px',
                  borderTopLeftRadius: '8px',
                  padding: '10px',
                  marginBottom: '15.5px',
                }}
              >
                <span
                  style={{
                    color: '#F3F4F6',
                    fontWeight: '600',
                    fontSize: '12px',
                  }}
                >
                  {handshakeID} No'lu İrsaliye Detay Bilgileri
                </span>
              </div>

              {/* İrsaliye No */}
              <div className="customRow">
                <text
                  style={{ fontSize: 14, fontWeight: '400', color: '#8890A0' }}
                >
                  İrsaliye No:
                </text>
                <text style={{ color: '#111827', fontSize: 14 }}>
                  {handshakeID}
                </text>
              </div>
              <div className="divider" />

              {/* Talep No */}
              <div className="customRow">
                <text
                  style={{ fontSize: 14, fontWeight: '400', color: '#8890A0' }}
                >
                  Talep No:
                </text>
                <text style={{ color: '#111827', fontSize: 14 }}>
                  {data.documentNo}
                </text>
              </div>
              <div className="divider" />

              {/* Barkod */}
              {data.barcode && (
                <>
                  <div className="customRow">
                    <text
                      style={{
                        fontSize: 14,
                        fontWeight: '400',
                        color: '#8890A0',
                      }}
                    >
                      Barkod:
                    </text>
                    <text style={{ color: '#111827', fontSize: 14 }}>
                      {data.barcode}
                    </text>
                  </div>
                  <div className="divider" />
                </>
              )}

              {/* Onay Tipi */}
              {data.confirmationType && (
                <>
                  <div className="customRow">
                    <text
                      style={{
                        fontSize: 14,
                        fontWeight: '400',
                        color: '#8890A0',
                      }}
                    >
                      Onay Tipi:
                    </text>
                    <text style={{ color: '#111827', fontSize: 14 }}>
                      {formatConfirmationType(data.confirmationType)}
                    </text>
                  </div>
                  <div className="divider" />
                </>
              )}

              {/* Gönderi Yönü */}
              {data.direction && (
                <>
                  <div className="customRow">
                    <text
                      style={{
                        fontSize: 14,
                        fontWeight: '400',
                        color: '#8890A0',
                      }}
                    >
                      Gönderi Yönü:
                    </text>
                    <text style={{ color: '#111827', fontSize: 14 }}>
                      {formatDirection(data.direction, data.fromLocationType)}
                    </text>
                  </div>
                  <div className="divider" />
                </>
              )}

              {/* Kargo Firması */}
              {data.shipmentFirm && (
                <>
                  <div className="customRow">
                    <text
                      style={{
                        fontSize: 14,
                        fontWeight: '400',
                        color: '#8890A0',
                      }}
                    >
                      Kargo Firması:
                    </text>
                    <text style={{ color: '#111827', fontSize: 14 }}>
                      {data.shipmentFirm}
                    </text>
                  </div>
                  <div className="divider" />
                </>
              )}

              {/* Paket Adedi */}
              {data.requestedInventoryCount && (
                <>
                  <div className="customRow">
                    <text
                      style={{
                        fontSize: 14,
                        fontWeight: '400',
                        color: '#8890A0',
                      }}
                    >
                      Paket Adedi:
                    </text>
                    <text style={{ color: '#111827', fontSize: 14 }}>
                      {data.requestedInventoryCount}
                    </text>
                  </div>
                  <div className="divider" />
                </>
              )}

              {/* AVM Adı */}
              {data.mallTitle && (
                <>
                  <div className="customRow">
                    <text
                      style={{
                        fontSize: 14,
                        fontWeight: '400',
                        color: '#8890A0',
                      }}
                    >
                      AVM Adı:
                    </text>
                    <text style={{ color: '#111827', fontSize: 14 }}>
                      {data.mallTitle}
                    </text>
                  </div>
                  <div className="divider" />
                </>
              )}

              {/* Mağaza Adı */}
              {data.branchTitle && (
                <>
                  <div className="customRow">
                    <text
                      style={{
                        fontSize: 14,
                        fontWeight: '400',
                        color: '#8890A0',
                      }}
                    >
                      Mağaza Adı:
                    </text>
                    <text style={{ color: '#111827', fontSize: 14 }}>
                      {data.branchTitle}
                    </text>
                  </div>
                  <div className="divider" />
                </>
              )}

              {/* Taşıyıcı (Maskelenmiş) */}
              {data.porterName && data.porterLastname && (
                <>
                  <div className="customRow">
                    <text
                      style={{
                        fontSize: 14,
                        fontWeight: '400',
                        color: '#8890A0',
                      }}
                    >
                      Taşıyıcı:
                    </text>
                    <text style={{ color: '#111827', fontSize: 14 }}>
                      {maskName(data.porterName)}{' '}
                      {maskName(data.porterLastname)}
                    </text>
                  </div>
                  <div className="divider" />
                </>
              )}

              {/* Araç Tipi ve Sayısı */}
              {data.details &&
                data.details.length > 0 &&
                data.details[0].transportationTool && (
                  <>
                    <div className="customRow">
                      <text
                        style={{
                          fontSize: 14,
                          fontWeight: '400',
                          color: '#8890A0',
                        }}
                      >
                        Araç Tipi ve Sayısı:
                      </text>
                      <text style={{ color: '#111827', fontSize: 14 }}>
                        {data.details[0].transportationTool} -{' '}
                        {data.details[0].transportationToolCount} Adet
                      </text>
                    </div>
                    <div className="divider" />
                  </>
                )}

              {/* Zimmete Alınma Tarihi (Yeni Alan) */}
              {data.onPorterDateTime && (
                <>
                  <div className="customRow">
                    <text
                      style={{
                        fontSize: 14,
                        fontWeight: '400',
                        color: '#8890A0',
                      }}
                    >
                      Zimmete Alınma Tarihi:
                    </text>
                    <text style={{ color: '#111827', fontSize: 14 }}>
                      {formatDate(data.onPorterDateTime)}
                    </text>
                  </div>
                  <div className="divider" />
                </>
              )}

              {/* Onaylanma Tarihi */}
              {data.handshakeConfirmedDateTime && (
                <>
                  <div className="customRow">
                    <text
                      style={{
                        fontSize: 14,
                        fontWeight: '400',
                        color: '#8890A0',
                      }}
                    >
                      Onaylanma Tarihi:
                    </text>
                    <text style={{ color: '#111827', fontSize: 14 }}>
                      {formatDate(data.handshakeConfirmedDateTime)}
                    </text>
                  </div>
                  <div className="divider" />
                </>
              )}

              {data.completedDateTime && (
                <>
                  <div className="customRow">
                    <text
                      style={{
                        fontSize: 14,
                        fontWeight: '400',
                        color: '#8890A0',
                      }}
                    >
                      Tamamlanma Tarihi:
                    </text>
                    <text style={{ color: '#111827', fontSize: 14 }}>
                      {formatDate(data.completedDateTime)}
                    </text>
                  </div>
                  <div className="divider" />
                </>
              )}

              {/* İş Emri Durumu */}
              {data.postStatus && (
                <>
                  <div className="customRow">
                    <text
                      style={{
                        fontSize: 14,
                        fontWeight: '400',
                        color: '#8890A0',
                      }}
                    >
                      İş Emri Durumu:
                    </text>
                    <text
                      style={{
                        backgroundColor:
                          data.postStatus === '1'
                            ? 'rgb(77 177 88)'
                            : '#DA3831',
                        color: 'white',
                        padding: 3,
                        borderRadius: 5,
                        paddingInline: 10,
                        fontSize: '12px',
                      }}
                    >
                      {formatPostStatus(data.postStatus)}
                    </text>
                  </div>
                </>
              )}
            </div>
          </Grid>
          {/* SAĞ TARAF: FİYAT VE HİZMET TABLOSU */}
          {/*
          <Grid item xs={12} md={5}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px'
              }}
            >
              <div
                style={{
                  minHeight: '50px',
                  width: '100%',
                  backgroundColor: '#172955',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <span style={{ color: '#F3F4F6', fontWeight: '600', fontSize: '12px' }}>
                  Hizmet ve Tutar Bilgileri
                </span>
              </div>
              <Table aria-label="price table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                    <TableCell>Hizmet</TableCell>
                    <TableCell align="right">Miktar</TableCell>
                    <TableCell align="right">Birim Fiyat</TableCell>
                    <TableCell align="right">Tutar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pricingData.rows.length > 0 ? (
                    pricingData.rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell component="th" scope="row" sx={{ color: '#111827' }}>
                          {row.toolName}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#111827' }}>{row.count}</TableCell>
                        <TableCell align="right" sx={{ color: '#111827' }}>
                          {row.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {row.currency}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#111827' }}>
                          {row.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {row.currency}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">Fiyat bilgisi bulunamadı</TableCell>
                    </TableRow>
                  )}

                  <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
                    <TableCell colSpan={3} align="left" sx={{ fontWeight: 'bold', color: '#172955' }}>
                      TOPLAM
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#172955' }}>
                      {pricingData.totalSum.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {pricingData.currencySymbol}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          */}
        </Grid>
      </Container>
    </>
  );
}
